import { formatRpcError } from "@/lib/function";
import store from "@/store";
import { EDocumentStatus, IDocument, IFolder } from "@/types";
import { readContract, waitForTransaction, writeContract } from "@wagmi/core";
import { isArray, isNumber } from "lodash";

const setupConfig = () => {
  const { verifyDocumentManager } =
    store.getState()?.contract?.infoWagmiConfig ?? {};
  return verifyDocumentManager;
};

const getFolder = async (id: number) => {
  try {
    const result = await readContract({
      ...setupConfig(),
      functionName: "read",
      args: [id],
    });

    const [
      folderName,
      folderOwner,
      fileName,
      fileHash,
      fileStatus,
      fileCreateTime,
      fileUpdateTime,
      folderCreateTime,
      folderUpdateTime,
    ] = result as Array<any>;

    if (!folderName) return null;

    const folder = {
      id,
      folderName,
      fileName: isArray(fileName) ? fileName : [],
      fileHash: isArray(fileHash) ? fileHash : [],
      fileStatus: isArray(fileStatus)
        ? fileStatus.map((status) => Number(status))
        : [],
      fileCreateTime: isArray(fileCreateTime)
        ? fileCreateTime.map((time) => Number(time) * 1000)
        : [],
      fileUpdateTime: isArray(fileUpdateTime)
        ? fileUpdateTime.map((time) => Number(time) * 1000)
        : [],
      folderCreateTime: Number(folderCreateTime),
      folderUpdateTime: Number(folderUpdateTime),
      folderOwner,
    } as IFolder;

    return folder;
  } catch (error) {
    return null;
  }
};

const getFolders = async (address: string) => {
  try {
    const result = await readContract({
      ...setupConfig(),
      functionName: "getFolderIDsByFolderOwner",
      args: [address],
    });

    const ids = isArray(result) ? result.map((id) => Number(id)) : [];

    const promises = ids.map(getFolder);
    const folders = await Promise.all(promises);
    const data = folders.filter((item) => item !== null);

    return data;
  } catch (error) {
    console.log(error);
    return [];
  }
};

const createFolder = async ({ folderName }: { folderName: string }) => {
  try {
    const { hash } = await writeContract({
      ...setupConfig(),
      functionName: "write",
      args: [folderName, [], []],
    });

    const tx = await waitForTransaction({ hash });
    return tx;
  } catch (error) {
    throw new Error(formatRpcError(error));
  }
};

const uploadFiles = async ({
  folderNumber,
  folderName,
  hashes,
  fileNames,
}: {
  folderName: string;
  folderNumber: number;
  fileNames: string[];
  hashes: string[];
}) => {
  try {
    const { hash } = await writeContract({
      ...setupConfig(),
      functionName: "write",
      args: [folderName, fileNames, hashes],
    });

    const tx = await waitForTransaction({ hash });
    return tx;
  } catch (error) {
    throw new Error(formatRpcError(error));
  }
};

const deleteFile = async (document: IDocument) => {
  try {
    const { hash } = await writeContract({
      ...setupConfig(),
      functionName: "updateFileStatus",
      args: [document.folderId, document.id, EDocumentStatus.Deleted],
    });

    const tx = await waitForTransaction({ hash });
    return tx;
  } catch (error) {
    throw new Error(formatRpcError(error));
  }
};

const updateFile = async ({
  document,
  newFileName,
  newHash,
}: {
  document: IDocument;
  newHash: string;
  newFileName: string;
}) => {
  try {
    const { hash } = await writeContract({
      ...setupConfig(),
      functionName: "updateFile",
      args: [document.folderId, document.id, newFileName, newHash],
    });

    const tx = await waitForTransaction({ hash });
    return tx;
  } catch (error) {
    throw new Error(formatRpcError(error));
  }
};

const getFolderByHash = async (hash: string) => {
  try {
    const result = await readContract({
      ...setupConfig(),
      functionName: "getFolderIdByFileHash",
      args: [hash],
    });

    return isNumber(Number(result)) ? Number(result) : 0;
  } catch (error) {
    return 0;
  }
};

export const documentService = {
  createFolder,
  uploadFiles,
  getFolders,
  getFolderByHash,
  deleteFile,
  updateFile,
  getFolder,
};
